import { z } from "zod";

export const roleSchema = z.enum(["SYSTEM_ADMIN", "GENERAL"]);

/**
 * API が返すログイン中の人(SPEC §2)。displayName はユーザーマスタの名前(「姓 名」)で、
 * 未設定ならメール。nameRegistered が false なら、画面で名前の登録を促す(2026-10-15)。
 */
export const portalUserViewSchema = z.object({
  email: z.email(),
  displayName: z.string(),
  nameRegistered: z.boolean().default(false),
  /** 自分の名前の各項目(本人が設定画面で変えるため)。ユーザーマスタに記録が無ければ null。 */
  profile: z
    .object({
      familyName: z.string(),
      givenName: z.string(),
      familyNameKana: z.string(),
      givenNameKana: z.string(),
      revision: z.number().int(),
    })
    .nullable()
    .default(null),
  roles: z.array(roleSchema),
});
export type PortalUserView = z.infer<typeof portalUserViewSchema>;

export const meResponseSchema = z.object({ user: portalUserViewSchema });
export type MeResponse = z.infer<typeof meResponseSchema>;

const NAME_MAX = 50;
/** 名前(姓・名とよみがな)。4 項目とも必須で、よみがなはひらがな(カタカナはサーバーでひらがなにそろえる)。 */
export const portalUserNameSchema = z.object({
  familyName: z.string().trim().min(1, "姓を入力してください").max(NAME_MAX),
  givenName: z.string().trim().min(1, "名を入力してください").max(NAME_MAX),
  familyNameKana: z.string().trim().min(1, "せいを入力してください").max(NAME_MAX),
  givenNameKana: z.string().trim().min(1, "めいを入力してください").max(NAME_MAX),
});
export type PortalUserNameInput = z.infer<typeof portalUserNameSchema>;

/** ユーザーマスタの 1 人。名前が未設定なら姓・名は空で、displayName はメール。 */
export const portalUserRecordSchema = z.object({
  email: z.email(),
  familyName: z.string(),
  givenName: z.string(),
  familyNameKana: z.string(),
  givenNameKana: z.string(),
  displayName: z.string(),
  nameRegistered: z.boolean(),
  lastLoginAt: z.iso.datetime().nullable(),
  /** アーカイブ(退職などで一覧や選択肢から外す)。 */
  archived: z.boolean().default(false),
  revision: z.number().int(),
});
export type PortalUserRecord = z.infer<typeof portalUserRecordSchema>;

export const portalUserListResponseSchema = z.object({ users: z.array(portalUserRecordSchema) });
export type PortalUserListResponse = z.infer<typeof portalUserListResponseSchema>;
export const portalUserResponseSchema = z.object({ user: portalUserRecordSchema });
export type PortalUserResponse = z.infer<typeof portalUserResponseSchema>;

/** まだログインしていない人を、名前つきで先に登録する(全体の管理者)。 */
export const createPortalUserRequestSchema = portalUserNameSchema.extend({
  email: z.email("メールアドレスの形式が正しくありません").max(320),
});
export type CreatePortalUserRequest = z.infer<typeof createPortalUserRequestSchema>;

/** 名前を設定・変更する(本人は自分の名前を `PATCH /api/me/name`、全体の管理者は誰でも)。 */
export const updatePortalUserRequestSchema = portalUserNameSchema.extend({
  revision: z.number().int().positive(),
});
export type UpdatePortalUserRequest = z.infer<typeof updatePortalUserRequestSchema>;

/** アーカイブする・戻す(全体の管理者)。 */
export const setPortalUserArchivedRequestSchema = z.object({
  archived: z.boolean(),
  revision: z.number().int().positive(),
});
export type SetPortalUserArchivedRequest = z.infer<typeof setPortalUserArchivedRequestSchema>;
